import { useState } from "react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import DiscountCodeInput from "../components/DiscountCodeInput";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ShoppingCart,
  Zap,
} from "lucide-react";

const planOptions = [
  {
    name: "Core",
    price: "$750",
    icon: <ShoppingCart className="h-4 w-4" />,
    features: [
      "1 assessment per month",
      "12-month commitment",
      "Billed quarterly ($2,250/quarter)",
      "Overage at $750/assessment",
    ],
  },
  {
    name: "Pro",
    price: "$500",
    icon: <Zap className="h-4 w-4" />,
    features: [
      "3 assessments per month",
      "12-month commitment",
      "Billed quarterly ($4,500/quarter)",
      "Overage at $500/assessment",
    ],
  },
];

const SignUpPlanStep = ({ selectedPlan, onSelectPlan, onBack, onComplete }) => {
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  const getDiscountedPrice = (basePrice) => {
    if (!appliedDiscount) return basePrice;
    return basePrice * (1 - appliedDiscount.percentage / 100);
  };

  const corePrice = getDiscountedPrice(750);
  const proPrice = getDiscountedPrice(500);
  const coreQuarterly = getDiscountedPrice(2250);
  const proQuarterly = getDiscountedPrice(4500);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={onBack}
          className="text-white/70 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <p className="text-sm font-medium text-white">Choose your plan</p>
      </div>

      <div className="space-y-3">
        {planOptions.map((plan) => {
          const isSelected = selectedPlan === plan.name;
          const basePrice = plan.name === "Core" ? 750 : 500;
          const discountedPrice = plan.name === "Core" ? corePrice : proPrice;
          const discountedQuarterly =
            plan.name === "Core" ? coreQuarterly : proQuarterly;
          const hasDiscount =
            appliedDiscount && appliedDiscount.plans.includes(plan.name);

          const dynamicFeatures = plan.features.map((f) => {
            if (hasDiscount) {
              if (f.includes("$2,250"))
                return f.replace(
                  "$2,250",
                  `$${discountedQuarterly.toLocaleString()}`,
                );
              if (f.includes("$4,500"))
                return f.replace(
                  "$4,500",
                  `$${discountedQuarterly.toLocaleString()}`,
                );
              if (f.includes("$750/assessment"))
                return f.replace(
                  "$750/assessment",
                  `$${discountedPrice}/assessment`,
                );
              if (f.includes("$500/assessment"))
                return f.replace(
                  "$500/assessment",
                  `$${discountedPrice}/assessment`,
                );
            }
            return f;
          });

          return (
            <button
              key={plan.name}
              type="button"
              onClick={() => onSelectPlan(plan.name)}
              className={`w-full text-left rounded-lg border p-4 transition-colors ${
                isSelected
                  ? "border-primary bg-primary/15"
                  : "border-white/10 bg-white/5 hover:border-white/25"
              }`}
            >
              {isSelected && (
                <Badge className="mb-2 text-[10px] bg-primary text-primary-foreground">
                  Selected
                </Badge>
              )}
              <div className="flex items-center gap-2 mb-1">
                <span className="text-primary">{plan.icon}</span>
                <span className="font-semibold text-white">{plan.name}</span>
              </div>
              <p className="text-xl font-bold text-white mb-2">
                {hasDiscount ? (
                  <>
                    <span className="line-through text-white/40 text-base mr-2">
                      {plan.price}
                    </span>
                    ${Math.round(discountedPrice)}
                  </>
                ) : (
                  plan.price
                )}
                <span className="text-sm font-normal text-white/50">
                  /assessment
                </span>
              </p>
              <div className="space-y-1.5">
                {dynamicFeatures.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-sm text-white/70"
                  >
                    <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {/* Discount Code */}
      <DiscountCodeInput plan={selectedPlan} onApply={setAppliedDiscount} />

      {/* Overages info */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-accent shrink-0 mt-0.5" />
          <p className="text-[11px] text-white/50 leading-relaxed">
            If you exceed your quarterly limit, you may continue at the same
            per-assessment rate. You'll be prompted before each overage charge.
          </p>
        </div>
      </div>

      <Button onClick={onComplete} className="w-full text-white">
        Create Account — {selectedPlan} Plan
      </Button>
    </div>
  );
};

export default SignUpPlanStep;
